/**
 * RecipeManager Component
 * 
 * Purpose: Admin panel for managing recipes (create, edit, delete)
 * Features: Form with all recipe fields, photo upload, ingredient management, publish toggle
 */

import React, { useState, useEffect } from 'react';
import config from '../config/environments';

const API_URL = config.api.baseUrl;

// Get admin token from environment or localStorage
const getAdminToken = () => {
  return process.env.REACT_APP_ADMIN_TOKEN || localStorage.getItem('admin_token') || '';
};

const RecipeManager = ({ onBack }) => {
  const [recipes, setRecipes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [currentRecipe, setCurrentRecipe] = useState(null);
  const [showForm, setShowForm] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    ingredients: [{ name: '', quantity: '', unit: '' }],
    instructions: '',
    cook_time_minutes: '',
    prep_time_minutes: '',
    servings: 4,
    source_url: '',
    tags: [],
    admin_rating: '',
    admin_notes: '',
    is_published: true
  });
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  // Available tags
  const availableTags = [
    'Breakfast',
    'Lunch',
    'Dinner',
    'Dessert',
    'Shabbat',
    'Quick Meals',
    'Vegetarian',
    'Meat',
    'Dairy',
    'Pareve',
    'Appetizer',
    'Side Dish',
    'Soup',
    'Salad',
    'Pasta',
    'Baking'
  ];

  useEffect(() => {
    fetchRecipes();
  }, []);

  const fetchRecipes = async () => {
    try {
      setIsLoading(true);
      const response = await fetch(`${API_URL}/api/recipes`, {
        headers: {
          'x-admin-token': getAdminToken()
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch recipes');
      }
      
      const data = await response.json();
      
      if (data.success) {
        setRecipes(data.recipes);
      }
    } catch (err) {
      console.error('Error fetching recipes:', err);
      setError('Failed to load recipes');
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      ingredients: [{ name: '', quantity: '', unit: '' }],
      instructions: '',
      cook_time_minutes: '',
      prep_time_minutes: '',
      servings: 4,
      source_url: '',
      tags: [],
      admin_rating: '',
      admin_notes: '',
      is_published: true
    });
    setPhotoFile(null);
    setPhotoPreview(null);
    setIsEditMode(false);
    setCurrentRecipe(null);
  };

  const handleNewRecipe = () => {
    resetForm();
    setShowForm(true);
  };

  const handleEditRecipe = (recipe) => {
    // Parse ingredients
    let parsedIngredients = [];
    try {
      if (typeof recipe.ingredients === 'string') {
        parsedIngredients = JSON.parse(recipe.ingredients);
      } else {
        parsedIngredients = recipe.ingredients;
      }
    } catch (e) {
      console.error('Error parsing ingredients:', e);
      parsedIngredients = [{ name: '', quantity: '', unit: '' }];
    }

    setFormData({
      name: recipe.name || '',
      description: recipe.description || '',
      ingredients: parsedIngredients.length > 0 ? parsedIngredients : [{ name: '', quantity: '', unit: '' }],
      instructions: recipe.instructions || '',
      cook_time_minutes: recipe.cook_time_minutes || '',
      prep_time_minutes: recipe.prep_time_minutes || '',
      servings: recipe.servings || 4,
      source_url: recipe.source_url || '',
      tags: recipe.tags || [],
      admin_rating: recipe.admin_rating || '',
      admin_notes: recipe.admin_notes || '',
      is_published: recipe.is_published !== false
    });
    
    if (recipe.photo_url) {
      setPhotoPreview(`${API_URL}${recipe.photo_url}`);
    }
    
    setCurrentRecipe(recipe);
    setIsEditMode(true);
    setShowForm(true);
  };

  const handleDeleteRecipe = async (recipeId) => {
    if (!window.confirm('Are you sure you want to delete this recipe? This cannot be undone.')) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/recipes/${recipeId}`, {
        method: 'DELETE',
        headers: {
          'x-admin-token': getAdminToken()
        }
      });

      const data = await response.json();

      if (data.success) {
        alert('Recipe deleted successfully!');
        fetchRecipes();
      } else {
        alert(`Error: ${data.message}`);
      }
    } catch (err) {
      console.error('Error deleting recipe:', err);
      alert('Failed to delete recipe');
    }
  };

  const handleFormChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleIngredientChange = (index, field, value) => {
    const newIngredients = [...formData.ingredients];
    newIngredients[index][field] = value;
    setFormData(prev => ({
      ...prev,
      ingredients: newIngredients
    }));
  };

  const addIngredient = () => {
    setFormData(prev => ({
      ...prev,
      ingredients: [...prev.ingredients, { name: '', quantity: '', unit: '' }]
    }));
  };

  const removeIngredient = (index) => {
    if (formData.ingredients.length === 1) {
      alert('You must have at least one ingredient');
      return;
    }
    const newIngredients = formData.ingredients.filter((_, i) => i !== index);
    setFormData(prev => ({
      ...prev,
      ingredients: newIngredients
    }));
  };

  const toggleTag = (tag) => {
    if (formData.tags.includes(tag)) {
      setFormData(prev => ({
        ...prev,
        tags: prev.tags.filter(t => t !== tag)
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, tag]
      }));
    }
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setPhotoFile(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.name.trim()) {
      alert('Recipe name is required');
      return;
    }
    if (!formData.instructions.trim()) {
      alert('Instructions are required');
      return;
    }
    if (formData.ingredients.filter(ing => ing.name.trim()).length === 0) {
      alert('At least one ingredient is required');
      return;
    }

    setIsSaving(true);

    try {
      // Prepare form data for multipart upload
      const submitData = new FormData();
      submitData.append('name', formData.name);
      submitData.append('description', formData.description);
      
      // Filter out empty ingredients
      const validIngredients = formData.ingredients.filter(ing => ing.name.trim());
      submitData.append('ingredients', JSON.stringify(validIngredients));
      
      submitData.append('instructions', formData.instructions);
      
      if (formData.cook_time_minutes) {
        submitData.append('cook_time_minutes', formData.cook_time_minutes);
      }
      if (formData.prep_time_minutes) {
        submitData.append('prep_time_minutes', formData.prep_time_minutes);
      }
      submitData.append('servings', formData.servings);
      
      if (formData.source_url) {
        submitData.append('source_url', formData.source_url);
      }
      
      submitData.append('tags', JSON.stringify(formData.tags));
      
      if (formData.admin_rating) {
        submitData.append('admin_rating', formData.admin_rating);
      }
      if (formData.admin_notes) {
        submitData.append('admin_notes', formData.admin_notes);
      }
      submitData.append('is_published', formData.is_published);
      
      if (photoFile) {
        submitData.append('photo', photoFile);
      }

      const url = isEditMode 
        ? `${API_URL}/api/recipes/${currentRecipe.id}`
        : `${API_URL}/api/recipes`;
      
      const method = isEditMode ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method: method,
        headers: {
          'x-admin-token': getAdminToken()
        },
        body: submitData
      });

      const data = await response.json();

      if (data.success) {
        alert(isEditMode ? 'Recipe updated successfully!' : 'Recipe created successfully!');
        resetForm();
        setShowForm(false);
        fetchRecipes();
      } else {
        alert(`Error: ${data.message}`);
      }
    } catch (err) {
      console.error('Error saving recipe:', err);
      alert('Failed to save recipe');
    } finally {
      setIsSaving(false);
    }
  };

  // Recipe List View
  if (!showForm) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-rose-50 p-6">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                {onBack && (
                  <button
                    onClick={onBack}
                    className="mr-4 p-2 hover:bg-gray-100 rounded-full transition-all"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>
                )}
                <div>
                  <h1 className="text-3xl font-bold text-gray-800">Recipe Manager</h1>
                  <p className="text-gray-600">Manage your community recipe book</p>
                </div>
              </div>
              <button
                onClick={handleNewRecipe}
                className="px-6 py-3 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-lg font-semibold hover:from-green-600 hover:to-emerald-600 transition-all shadow-lg flex items-center"
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                </svg>
                Add New Recipe
              </button>
            </div>
          </div>

          {/* Recipe List */}
          {isLoading ? (
            <div className="text-center py-12">
              <div className="text-5xl mb-4 animate-bounce">👨‍🍳</div>
              <p className="text-gray-600">Loading recipes...</p>
            </div>
          ) : error ? (
            <div className="bg-red-50 border-2 border-red-200 rounded-xl p-6 text-center">
              <p className="text-red-800">{error}</p>
            </div>
          ) : recipes.length === 0 ? (
            <div className="bg-white rounded-xl shadow-lg p-12 text-center">
              <div className="text-6xl mb-4">📖</div>
              <h3 className="text-2xl font-bold text-gray-800 mb-2">No Recipes Yet</h3>
              <p className="text-gray-600 mb-6">Start building your recipe collection!</p>
              <button
                onClick={handleNewRecipe}
                className="px-6 py-3 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-lg font-semibold hover:from-green-600 hover:to-emerald-600 transition-all"
              >
                Create Your First Recipe
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {recipes.map(recipe => (
                <div key={recipe.id} className="bg-white rounded-xl shadow-lg overflow-hidden">
                  {/* Recipe Image */}
                  <div className="h-48 bg-gray-200 relative">
                    {recipe.photo_url ? (
                      <img
                        src={`${API_URL}${recipe.photo_url}`}
                        alt={recipe.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-6xl">
                        🍽️
                      </div>
                    )}
                    {!recipe.is_published && (
                      <div className="absolute top-3 right-3 bg-red-500 text-white px-3 py-1 rounded-full text-sm font-bold">
                        DRAFT
                      </div>
                    )}
                  </div>

                  {/* Recipe Details */}
                  <div className="p-4">
                    <h3 className="text-xl font-bold text-gray-800 mb-2 line-clamp-1">
                      {recipe.name}
                    </h3>
                    {recipe.description && (
                      <p className="text-gray-600 text-sm mb-3 line-clamp-2">
                        {recipe.description}
                      </p>
                    )}

                    {/* Tags */}
                    {recipe.tags && recipe.tags.length > 0 && (
                      <div className="flex flex-wrap gap-1 mb-3">
                        {recipe.tags.slice(0, 3).map((tag, idx) => (
                          <span
                            key={idx}
                            className="px-2 py-1 bg-purple-100 text-purple-800 text-xs rounded-full"
                          >
                            {tag}
                          </span>
                        ))}
                        {recipe.tags.length > 3 && (
                          <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">
                            +{recipe.tags.length - 3}
                          </span>
                        )}
                      </div>
                    )}

                    {/* Actions */}
                    <div className="flex gap-2 pt-3 border-t border-gray-200">
                      <button
                        onClick={() => handleEditRecipe(recipe)}
                        className="flex-1 px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg font-medium transition-all"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDeleteRecipe(recipe.id)}
                        className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg font-medium transition-all"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  // Recipe Form View
  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-rose-50 p-6">
      <div className="max-w-4xl mx-auto">
        {/* Form Header */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <button
                onClick={() => {
                  if (window.confirm('Discard changes?')) {
                    setShowForm(false);
                    resetForm();
                  }
                }}
                className="mr-4 p-2 hover:bg-gray-100 rounded-full transition-all"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <div>
                <h1 className="text-3xl font-bold text-gray-800">
                  {isEditMode ? 'Edit Recipe' : 'New Recipe'}
                </h1>
                <p className="text-gray-600">Fill in the details below</p>
              </div>
            </div>
          </div>
        </div>

        {/* Recipe Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Info */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h2 className="text-xl font-bold text-gray-800 mb-4">Basic Information</h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Recipe Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleFormChange('name', e.target.value)}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-purple-500 focus:outline-none"
                  placeholder="e.g., Classic Challah"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => handleFormChange('description', e.target.value)}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-purple-500 focus:outline-none"
                  rows="3"
                  placeholder="Brief description of the recipe..."
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Instagram URL (optional)
                </label>
                <input
                  type="url"
                  value={formData.source_url}
                  onChange={(e) => handleFormChange('source_url', e.target.value)}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-purple-500 focus:outline-none"
                  placeholder="https://instagram.com/p/..."
                />
              </div>
            </div>
          </div>

          {/* Photo Upload */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h2 className="text-xl font-bold text-gray-800 mb-4">Recipe Photo</h2>
            
            <div className="space-y-4">
              {photoPreview && (
                <div className="relative w-full h-64 rounded-lg overflow-hidden bg-gray-100">
                  <img
                    src={photoPreview}
                    alt="Recipe preview"
                    className="w-full h-full object-cover"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      setPhotoFile(null);
                      setPhotoPreview(null);
                    }}
                    className="absolute top-3 right-3 p-2 bg-red-500 text-white rounded-full hover:bg-red-600 transition-all"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              )}

              <div>
                <label className="block w-full px-4 py-8 border-2 border-dashed border-gray-300 rounded-lg text-center cursor-pointer hover:border-purple-500 hover:bg-purple-50 transition-all">
                  <svg className="w-12 h-12 mx-auto mb-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <span className="text-gray-600">
                    {photoPreview ? 'Change Photo' : 'Upload Photo'}
                  </span>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handlePhotoChange}
                    className="hidden"
                  />
                </label>
              </div>
            </div>
          </div>

          {/* Timing & Servings */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h2 className="text-xl font-bold text-gray-800 mb-4">Timing & Servings</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Prep Time (minutes)
                </label>
                <input
                  type="number"
                  value={formData.prep_time_minutes}
                  onChange={(e) => handleFormChange('prep_time_minutes', e.target.value)}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-purple-500 focus:outline-none"
                  placeholder="30"
                  min="0"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Cook Time (minutes)
                </label>
                <input
                  type="number"
                  value={formData.cook_time_minutes}
                  onChange={(e) => handleFormChange('cook_time_minutes', e.target.value)}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-purple-500 focus:outline-none"
                  placeholder="45"
                  min="0"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Servings
                </label>
                <input
                  type="number"
                  value={formData.servings}
                  onChange={(e) => handleFormChange('servings', e.target.value)}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-purple-500 focus:outline-none"
                  placeholder="4"
                  min="1"
                />
              </div>
            </div>
          </div>

          {/* Ingredients */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-800">Ingredients *</h2>
              <button
                type="button"
                onClick={addIngredient}
                className="px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg font-medium transition-all flex items-center"
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                </svg>
                Add Ingredient
              </button>
            </div>

            <div className="space-y-3">
              {formData.ingredients.map((ingredient, index) => (
                <div key={index} className="flex gap-2 items-start">
                  <input
                    type="text"
                    value={ingredient.quantity}
                    onChange={(e) => handleIngredientChange(index, 'quantity', e.target.value)}
                    className="w-24 px-3 py-2 border-2 border-gray-200 rounded-lg focus:border-purple-500 focus:outline-none"
                    placeholder="2"
                  />
                  <input
                    type="text"
                    value={ingredient.unit}
                    onChange={(e) => handleIngredientChange(index, 'unit', e.target.value)}
                    className="w-32 px-3 py-2 border-2 border-gray-200 rounded-lg focus:border-purple-500 focus:outline-none"
                    placeholder="cups"
                  />
                  <input
                    type="text"
                    value={ingredient.name}
                    onChange={(e) => handleIngredientChange(index, 'name', e.target.value)}
                    className="flex-1 px-3 py-2 border-2 border-gray-200 rounded-lg focus:border-purple-500 focus:outline-none"
                    placeholder="flour"
                    required={index === 0}
                  />
                  {formData.ingredients.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeIngredient(index)}
                      className="p-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-all"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Instructions */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h2 className="text-xl font-bold text-gray-800 mb-4">Instructions *</h2>
            
            <textarea
              value={formData.instructions}
              onChange={(e) => handleFormChange('instructions', e.target.value)}
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-purple-500 focus:outline-none"
              rows="10"
              placeholder="Step-by-step instructions...&#10;&#10;1. Preheat oven to 350°F&#10;2. Mix dry ingredients...&#10;3. Bake for 45 minutes..."
              required
            />
          </div>

          {/* Tags */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h2 className="text-xl font-bold text-gray-800 mb-4">Categories / Tags</h2>
            
            <div className="flex flex-wrap gap-2">
              {availableTags.map(tag => (
                <button
                  key={tag}
                  type="button"
                  onClick={() => toggleTag(tag)}
                  className={`px-4 py-2 rounded-lg font-medium transition-all ${
                    formData.tags.includes(tag)
                      ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow-md'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {tag}
                </button>
              ))}
            </div>
          </div>

          {/* Rating & Notes */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h2 className="text-xl font-bold text-gray-800 mb-4">Your Review</h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Rating (1-5 stars)
                </label>
                <select
                  value={formData.admin_rating}
                  onChange={(e) => handleFormChange('admin_rating', e.target.value)}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-purple-500 focus:outline-none"
                >
                  <option value="">Not rated</option>
                  <option value="1">⭐ 1 Star</option>
                  <option value="2">⭐⭐ 2 Stars</option>
                  <option value="3">⭐⭐⭐ 3 Stars</option>
                  <option value="4">⭐⭐⭐⭐ 4 Stars</option>
                  <option value="5">⭐⭐⭐⭐⭐ 5 Stars</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Your Notes / Review
                </label>
                <textarea
                  value={formData.admin_notes}
                  onChange={(e) => handleFormChange('admin_notes', e.target.value)}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-purple-500 focus:outline-none"
                  rows="4"
                  placeholder="Share your thoughts about this recipe..."
                />
              </div>
            </div>
          </div>

          {/* Publish Toggle */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <label className="flex items-center justify-between cursor-pointer">
              <div>
                <h2 className="text-xl font-bold text-gray-800">Publish Recipe</h2>
                <p className="text-gray-600 text-sm">Make this recipe visible to the public</p>
              </div>
              <div className="relative">
                <input
                  type="checkbox"
                  checked={formData.is_published}
                  onChange={(e) => handleFormChange('is_published', e.target.checked)}
                  className="sr-only"
                />
                <div
                  onClick={() => handleFormChange('is_published', !formData.is_published)}
                  className={`w-14 h-8 rounded-full transition-all ${
                    formData.is_published ? 'bg-green-500' : 'bg-gray-300'
                  }`}
                >
                  <div
                    className={`w-6 h-6 bg-white rounded-full shadow-md transform transition-all mt-1 ${
                      formData.is_published ? 'translate-x-7' : 'translate-x-1'
                    }`}
                  />
                </div>
              </div>
            </label>
          </div>

          {/* Submit Button */}
          <div className="flex gap-4">
            <button
              type="button"
              onClick={() => {
                if (window.confirm('Discard changes?')) {
                  setShowForm(false);
                  resetForm();
                }
              }}
              className="flex-1 px-6 py-4 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-lg font-bold transition-all"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSaving}
              className="flex-1 px-6 py-4 bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white rounded-lg font-bold transition-all shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSaving ? 'Saving...' : isEditMode ? 'Update Recipe' : 'Create Recipe'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RecipeManager;

